"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { MdCheckCircle, MdAutoAwesome } from "react-icons/md";
import {
  TypeEnum,
  PurposeEnum,
  View,
  Orientation,
  EnergyRating,
} from "@/types/types";
import {
  facilityRules,
  getAvailableFacilities,
} from "@/lib/getFacilities/getAvailableFacilities";
import styles from "./amenities-section.module.css";
import { useListingContext } from "../listingContex";

export default function AmenitiesSection() {
  const tFac = useTranslations("NewListing.Facilities");
  const tView = useTranslations("NewListing.View");
  const tOri = useTranslations("NewListing.Orientation");
  const tEpc = useTranslations("NewListing.EPC");

  const { data, updateField, isUserAgencyOwner } = useListingContext();
  if (!data) return <AmenitiesSkeleton />;

  const isLand = data.type === TypeEnum.LAND;
  const isHouseOrApt =
    data.type === TypeEnum.APARTMENT || data.type === TypeEnum.HOUSE;

  // --- AMENITIES LOGIC ---
  const availableFacilities = getAvailableFacilities(data.type, data.purpose);
  const allFacilitiesSelected =
    availableFacilities.length > 0 &&
    availableFacilities.every((f) => data.facilities?.[f]);

  const handleSelectAllFacilities = (e: React.MouseEvent) => {
    e.preventDefault();
    const newFacilities = { ...data.facilities };
    availableFacilities.forEach((f) => {
      newFacilities[f] = !allFacilitiesSelected;
    });
    updateField("facilities", newFacilities);
  };

  const toggleFacility = (facility: keyof typeof data.facilities) => {
    updateField("facilities", {
      ...data.facilities,
      [facility]: !data.facilities?.[facility],
    });
  };

  // --- VIEWS ---
  const views: { key: keyof View; img: string; label: string }[] = [
    {
      key: "sea_view",
      img: "https://d1ktxmigjvpff7.cloudfront.net/view-icons/sea_view.png",
      label: tView("sea-view"),
    },
    {
      key: "mountain_view",
      img: "https://d1ktxmigjvpff7.cloudfront.net/view-icons/mountain_view.png",
      label: tView("mountain-view"),
    },
    {
      key: "countryside_view",
      img: "https://d1ktxmigjvpff7.cloudfront.net/view-icons/countryside_view.png",
      label: tView("countryside-view"),
    },
    {
      key: "city_view",
      img: "https://d1ktxmigjvpff7.cloudfront.net/view-icons/city_view.png",
      label: tView("city-view"),
    },
  ];

  // --- ORIENTATIONS ---
  const orientations: (keyof Orientation)[] = [
    "north",
    "south",
    "east",
    "west",
  ];

  // --- EPC ---
  const epcOptions: EnergyRating[] = ["A", "B", "C", "D", "E", "F", "G", "N/A"];

  return (
    <section className={styles.transparentContainer}>
      <h2 className={styles.sectionTitle}>Amenities & Features</h2>

      {/* 1. AMENITIES (FACILITIES) */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <h3 className={styles.subTitle}>{tFac("facilitiesTitle")}</h3>
            <p className={styles.subDescription}>
              {tFac("facilitiesDescription")}
            </p>
          </div>
          {availableFacilities.length > 0 && (
            <button
              className={styles.selectAllBtn}
              onClick={handleSelectAllFacilities}
            >
              {allFacilitiesSelected ? "Deselect All" : "Select All"}
            </button>
          )}
        </div>

        <div className={styles.pillGrid}>
          {/* Luxury Finishes Special Toggle */}
          {isHouseOrApt && (
            <button
              onClick={(e) => {
                e.preventDefault();
                updateField("has_luxury_finishes", !data.has_luxury_finishes);
              }}
              className={`${styles.pillCard} ${data.has_luxury_finishes ? styles.pillCardActive : ""}`}
            >
              {data.has_luxury_finishes && (
                <div className={styles.checkBadge}>
                  <MdCheckCircle size={18} />
                </div>
              )}
              <div className={styles.iconPlaceholder}>
                <MdAutoAwesome size={22} color="#f59e0b" />
              </div>
              <span className={styles.pillText}>{tFac("luxury-finishes")}</span>
            </button>
          )}

          {/* Standard Facilities Map */}
          {availableFacilities.map((facility) => {
            const isActive = !!data.facilities?.[facility];
            return (
              <button
                key={facility}
                onClick={(e) => {
                  e.preventDefault();
                  toggleFacility(facility as keyof typeof data.facilities);
                }}
                className={`${styles.pillCard} ${isActive ? styles.pillCardActive : ""}`}
              >
                {isActive && (
                  <div className={styles.checkBadge}>
                    <MdCheckCircle size={18} />
                  </div>
                )}
                <img
                  src={facilityRules[facility]?.icon_url}
                  alt={tFac(facilityRules[facility]?.label)}
                  className={styles.pillImage}
                />
                <span className={styles.pillText}>
                  {tFac(facilityRules[facility]?.label)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. VIEW */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <h3 className={styles.subTitle}>{tView("viewTitle")}</h3>
            <p className={styles.subDescription}>{tView("viewDescription")}</p>
          </div>
        </div>
        <div className={styles.pillGrid}>
          {views.map((v) => {
            const isActive = !!data.view?.[v.key];
            return (
              <button
                key={v.key}
                onClick={(e) => {
                  e.preventDefault();
                  updateField("view", { ...data.view, [v.key]: !isActive });
                }}
                className={`${styles.pillCard} ${isActive ? styles.pillCardActive : ""}`}
              >
                {isActive && (
                  <div className={styles.checkBadge}>
                    <MdCheckCircle size={18} />
                  </div>
                )}
                <img src={v.img} alt={v.label} className={styles.pillImage} />
                <span className={styles.pillText}>{v.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. ORIENTATION */}
      {!isLand && (
        <div className={styles.subSection}>
          <div className={styles.subSectionHeader}>
            <div>
              <h3 className={styles.subTitle}>{tOri("orientationTitle")}</h3>
              <p className={styles.subDescription}>
                {tOri("orientationDescription")}
              </p>
            </div>
          </div>
          <div className={styles.pillGridTextOnly}>
            {orientations.map((ori) => {
              const isActive = !!data.orientation?.[ori];
              return (
                <button
                  key={ori}
                  onClick={(e) => {
                    e.preventDefault();
                    updateField("orientation", {
                      ...data.orientation,
                      [ori]: !isActive,
                    });
                  }}
                  className={`${styles.textPill} ${isActive ? styles.textPillActive : ""}`}
                >
                  {isActive && (
                    <MdCheckCircle size={16} className={styles.inlineCheck} />
                  )}
                  <span>{tOri(ori)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 4. EPC (Energy Performance Certificate) */}
      {!isLand && (
        <div className={styles.subSection}>
          <div className={styles.subSectionHeader}>
            <div>
              <h3 className={styles.subTitle}>{tEpc("title")}</h3>
              <p className={styles.subDescription}>{tEpc("description")}</p>
            </div>
          </div>
          <div className={styles.epcGrid}>
            {epcOptions.map((grade) => {
              const isActive = data.epc === grade;
              // Clean grade name for CSS class (e.g., "N/A" -> "NA")
              const cssClass = grade === "N/A" ? "NA" : grade;

              return (
                <button
                  key={grade}
                  type="button" // Always specify type button to prevent form submission
                  onClick={(e) => {
                    e.preventDefault();
                    // Now TypeScript knows 'grade' is exactly an 'EnergyRating'
                    updateField("epc", grade);
                  }}
                  className={`${styles.epcBtn} ${styles[`epc${cssClass}`]} ${isActive ? styles.epcActive : ""}`}
                >
                  {grade}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export function AmenitiesSkeleton() {
  return (
    <section className={styles.transparentContainer}>
      <div className={`${styles.skeletonPulse} ${styles.skelSectionTitle}`} />

      {/* 1. AMENITIES SKELETON */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
          <div className={`${styles.skeletonPulse} ${styles.skelSelectAll}`} />
        </div>
        <div className={styles.pillGrid}>
          {/* Render ~12 dummy cards to simulate a typical amenities list */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`${styles.skeletonPulse} ${styles.pillCard}`}
            >
              <div
                className={`${styles.skeletonPulse} ${styles.skelPillIcon}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelPillText}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* 2. VIEW SKELETON */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
        </div>
        <div className={styles.pillGrid}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`${styles.skeletonPulse} ${styles.pillCard}`}
            >
              <div
                className={`${styles.skeletonPulse} ${styles.skelPillIcon}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelPillText}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* 3. ORIENTATION SKELETON */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
        </div>
        <div className={styles.pillGridTextOnly}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`${styles.skeletonPulse} ${styles.skelTextPill}`}
            />
          ))}
        </div>
      </div>

      {/* 4. EPC SKELETON */}
      <div className={styles.subSection}>
        <div className={styles.subSectionHeader}>
          <div>
            <div className={`${styles.skeletonPulse} ${styles.skelSubTitle}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelSubDesc}`} />
          </div>
        </div>
        <div className={styles.epcGrid}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className={`${styles.skeletonPulse} ${styles.skelEpcBtn}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
